import React from 'react';
import { Phone, CheckCircle, AlertTriangle, Settings } from 'lucide-react';
export function SteeringSuspension() {
  return <div className="min-h-screen bg-gray-50" data-id="element-1334">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#B1050B] to-[#8A0408] text-white py-16" data-id="element-1335">
        <div className="container mx-auto px-4" data-id="element-1336">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-center" data-id="element-1337">
            Front end and Suspension
          </h1>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12" data-id="element-1338">
        <div className="max-w-4xl mx-auto" data-id="element-1339">
          {/* What Everyone Should Know */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-1340">
            <h2 className="text-3xl font-bold text-[#B1050B] mb-6" data-id="element-1341">
              What Everyone Should Know About Wheel Alignment
            </h2>

            <div className="mb-6" data-id="element-1342">
              <img src="//drwzpk38qkpfb.cloudfront.net/www.garrettauto.com/uploaded/images/alignment'.jpg?opt=w3" alt="Wheel alignment diagram" className="max-w-[300px] h-auto rounded-lg float-right ml-6 mb-4" data-id="element-1343" />

              <p className="text-gray-700 mb-4" data-id="element-1344">
                On this vehicle, the front wheels are not aligned to the rear
                thrust line. This can happen from normal wear and stress,
                whether your vehicle has adjustable or non-adjustable rear
                suspension.
              </p>

              <p className="text-gray-700 mb-4" data-id="element-1345">
                To steer straight ahead, you would have to steer the front
                wheels slightly to the right.
              </p>

              <p className="text-gray-700 mb-4" data-id="element-1346">
                A common result would be that the vehicle would "dog track" and
                possibly "pull" to the side.
              </p>

              <p className="text-gray-700 mb-4" data-id="element-1347">
                Of course, the angles are exaggerated so you can more easily see
                the condition. But it takes only a small misalignment to create
                problems.
              </p>
            </div>
          </div>

          {/* Why Four Wheel Alignment */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-1348">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-1349">
              Why Four Wheel Alignment?
            </h2>

            <div className="space-y-6" data-id="element-1350">
              <div className="border-l-4 border-[#B1050B] pl-6" data-id="element-1351">
                <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-1352">
                  Reduced Tire Wear
                </h3>
                <p className="text-gray-700" data-id="element-1353">
                  Improper alignment is a major cause of premature tire wear.
                  Over the years, a properly aligned vehicle can add thousands
                  of miles to tire life.
                </p>
              </div>

              <div className="flex justify-center my-6" data-id="element-1354">
                <img src="//drwzpk38qkpfb.cloudfront.net/www.garrettauto.com/uploaded/images/tire-wear-problems-alignment-s.jpg400x400.jpg?opt=w3" alt="Tire wear problems from alignment" className="max-w-[300px] h-auto rounded-lg" data-id="element-1355" />
              </div>

              <div className="border-l-4 border-[#B1050B] pl-6" data-id="element-1356">
                <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-1357">
                  Better Gas Mileage
                </h3>
                <p className="text-gray-700" data-id="element-1358">
                  Gas mileage increases as rolling resistance decreases. Total
                  alignment sets all four wheels parallel which, along with
                  proper inflation, minimizes rolling resistance.
                </p>
              </div>

              <div className="border-l-4 border-[#B1050B] pl-6" data-id="element-1359">
                <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-1360">
                  Improved Handling
                </h3>
                <p className="text-gray-700" data-id="element-1361">
                  Does your car pull to one side? Does the steering wheel
                  vibrate? Do you constantly have to move the steering wheel to
                  keep your car traveling straight ahead? Many handling problems
                  can be corrected by total alignment.
                </p>
              </div>

              <div className="border-l-4 border-[#B1050B] pl-6" data-id="element-1362">
                <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-1363">
                  Safer Driving
                </h3>
                <p className="text-gray-700" data-id="element-1364">
                  A suspension system inspection is part of our alignment
                  procedure. This allows us to spot worn parts before they cause
                  costly problems.
                </p>
              </div>
            </div>
          </div>

          {/* Here's What We Do */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-1365">
            <h2 className="text-2xl font-bold text-[#B1050B] mb-6" data-id="element-1366">
              Here's what we do:
            </h2>

            {/* Non-Adjustable Rear Suspensions */}
            <div className="mb-8" data-id="element-1367">
              <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-1368">
                For vehicles with non-adjustable rear suspensions
              </h3>
              <ol className="space-y-3 list-decimal list-inside text-gray-700 mb-4" data-id="element-1369">
                <li data-id="element-1370">Angle readings are measured at all four wheels.</li>
                <li data-id="element-1371">The steering wheel is centered.</li>
                <li data-id="element-1372">
                  Front wheels are referenced to rear thrust line and set to
                  specifications.
                </li>
              </ol>
              <div className="bg-green-50 border-l-4 border-green-500 p-4" data-id="element-1373">
                <p className="text-gray-800 font-semibold" data-id="element-1374">
                  Result: All four wheels are parallel and the steering wheel is
                  centered.
                </p>
              </div>
            </div>

            {/* Adjustable Rear Suspensions */}
            <div data-id="element-1375">
              <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-1376">
                For vehicles with adjustable rear suspensions
              </h3>

              <div className="flex justify-center mb-6" data-id="element-1377">
                <img src="//drwzpk38qkpfb.cloudfront.net/www.garrettauto.com/uploaded/images/front-end-alignment-018.jpg400x400.jpg?opt=w3" alt="Front end alignment" className="max-w-[300px] h-auto rounded-lg" data-id="element-1378" />
              </div>

              <ol className="space-y-3 list-decimal list-inside text-gray-700 mb-4" data-id="element-1379">
                <li data-id="element-1380">Angle readings are measured at all four wheels.</li>
                <li data-id="element-1381">Rear wheels are set to specification.</li>
                <li data-id="element-1382">Steering wheel is centered.</li>
                <li data-id="element-1383">Front wheels are referenced to the rear thrust line.</li>
              </ol>
              <div className="bg-green-50 border-l-4 border-green-500 p-4" data-id="element-1384">
                <p className="text-gray-800 font-semibold" data-id="element-1385">
                  Result: all four wheels are positioned straight ahead and
                  parallel.
                </p>
              </div>
            </div>
          </div>

          {/* Important Questions */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-1386">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-1387">
              Important Questions About Wheel Alignment
            </h2>

            <div className="space-y-6" data-id="element-1388">
              <div className="border-l-4 border-blue-500 pl-6" data-id="element-1389">
                <p className="text-lg font-bold text-gray-900 mb-2" data-id="element-1390">
                  Q. How important is wheel alignment?
                </p>
                <p className="text-gray-700" data-id="element-1391">
                  <strong data-id="element-1392">A.</strong> A car with toe alignment just 0.34 degrees
                  out of specification can drag its tires sideways for more than
                  68 miles per year.
                </p>
              </div>

              <div className="border-l-4 border-blue-500 pl-6" data-id="element-1393">
                <p className="text-lg font-bold text-gray-900 mb-2" data-id="element-1394">
                  Q. What are the symptoms of misalignment?
                </p>
                <div className="text-gray-700" data-id="element-1395">
                  <p className="mb-2" data-id="element-1396">
                    <strong data-id="element-1397">A.</strong>
                  </p>
                  <ul className="space-y-2 ml-4" data-id="element-1398">
                    <li className="flex items-start" data-id="element-1399">
                      <AlertTriangle className="text-yellow-600 mr-2 flex-shrink-0 mt-1" size={16} data-id="element-1400" />
                      <span data-id="element-1401">Excessive or uneven tire wear</span>
                    </li>
                    <li className="flex items-start" data-id="element-1402">
                      <AlertTriangle className="text-yellow-600 mr-2 flex-shrink-0 mt-1" size={16} data-id="element-1403" />
                      <span data-id="element-1404">Vehicle pulls left or right</span>
                    </li>
                    <li className="flex items-start" data-id="element-1405">
                      <AlertTriangle className="text-yellow-600 mr-2 flex-shrink-0 mt-1" size={16} data-id="element-1406" />
                      <span data-id="element-1407">Steering wheel vibration</span>
                    </li>
                    <li className="flex items-start" data-id="element-1408">
                      <AlertTriangle className="text-yellow-600 mr-2 flex-shrink-0 mt-1" size={16} data-id="element-1409" />
                      <span data-id="element-1410">Loose or wandering steering</span>
                    </li>
                    <li className="flex items-start" data-id="element-1411">
                      <AlertTriangle className="text-yellow-600 mr-2 flex-shrink-0 mt-1" size={16} data-id="element-1412" />
                      <span data-id="element-1413">Steering wheel not centered</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="border-l-4 border-blue-500 pl-6" data-id="element-1414">
                <p className="text-lg font-bold text-gray-900 mb-2" data-id="element-1415">
                  Q. How often should alignment be checked?
                </p>
                <p className="text-gray-700" data-id="element-1416">
                  <strong data-id="element-1417">A.</strong> Every 10,000 miles or at least once per
                  year.
                </p>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white rounded-lg shadow-xl p-8 text-center" data-id="element-1418">
            <h2 className="text-3xl font-bold mb-4" data-id="element-1419">Need Alignment Service?</h2>
            <p className="text-xl mb-6" data-id="element-1420">
              <strong data-id="element-1421">If we can straighten you out give us a call</strong>
            </p>
            <a href="tel:2484788382" className="inline-flex items-center bg-[#B1050B] hover:bg-[#8A0408] text-white px-8 py-4 rounded-lg font-bold text-lg transition-colors shadow-lg" data-id="element-1422">
              <Phone className="mr-3" size={24} data-id="element-1423" />
              Call (248) 478-8382
            </a>
            <p className="mt-4 text-gray-300" data-id="element-1424">
              24175 Industrial Park Dr., Farmington Hills, MI 48335
            </p>
          </div>
        </div>
      </div>
    </div>;
}